import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { RestTemplateAreaModel, RestTemplateModel } from '@template/models/rest-template-model.interface';

export class TemplateEditorContent {
  areas: AreaModel[] = [];
  private _itemsChanged = new Subject<AreaModel[]>();
  itemsChanges$ = this._itemsChanged.asObservable();

  private _templateChanges = new Subject<void>();
  constructor(template?: RestTemplateModel) {
    if (template) {
      this.init(template);
    }
  }

  init(template: RestTemplateModel) {
    this.areas = template.inspectionTemplateAreas
      .sort((a, b) => {
        if (a.position > b.position) return 1;
        if (a.position < b.position) return -1;
        return 0;
      })
      .map(area => ({
        ...area,
        areaSurvey: JSON.parse(area.areaSurveyJson) as AreaSurveyModel,
      }));
  }

  addArea(area: RestTemplateAreaModel) {
    this.areas.push({
      ...area,
      areaSurvey: JSON.parse(area.areaSurveyJson) as AreaSurveyModel,
    });
  }

  removeArea(id: number) {
    this.areas = [...this.areas.filter(item => item.id !== id)];
  }

  updateArea(item: AreaModel) {
    this.areas = this.areas.map(innerItem => (innerItem.id !== item.id ? innerItem : { ...innerItem, ...item }));
  }

  onAreasChanged() {
    for (let i = 0; i < this.areas.length; i++) {
      this.areas[i].position = i;
    }
  }
}

export class TemplateEditorArea {
  items: AreaPageModel[];

  constructor(area: AreaModel) {
    this.items = area.areaSurvey.pages ? [...area.areaSurvey.pages] : [];
  }
  addItem(title: string) {
    const name = `page${this.items.length + 1}`;
    this.items.push({
      name,
      title,
      elements: [
        // default invisible items for mobile
        {
          type: 'file',
          name: 'Pictures:',
          title: 'Pictures:',
          description: 'Pictures:',
        },
        {
          type: 'comment',
          name: 'Enter a note:',
          title: 'Enter a note:',
        },
      ],
    });
    return name;
  }

  removeItem(name: string) {
    this.items = [...this.items.filter(item => item.name !== name)];
  }

  updateItem(item: AreaPageModel) {
    this.items = this.items.map(innerItem => (innerItem.name !== item.name ? innerItem : { ...innerItem, ...item }));
  }

  addRatingGroup(item: AreaPageModel, group: TemplateEditorRatingGroup) {
    const foundItem = this.items.find(innerItem => item.name === innerItem.name);
    if (!foundItem) {
      return;
    }

    const items = _.cloneDeep(group.items).map((item, index) => {
      return { item: { ...item.item, isDefault: index === 0 } };
    });

    const newRatingElement: AreaPageElementModel = {
      type: 'propertystate',
      name: group.name,
      title: group.name,
      isRequired: true,
      guid: uuidv4(),
      items: items,
    };
    this.updateItem({ ...foundItem, elements: [...(foundItem.elements ?? []), newRatingElement] });
  }
  removeRatingGroup(item: AreaPageModel) {
    const foundItem = this.items.find(innerItem => item.name === innerItem.name);
    if (!foundItem) {
      return;
    }
    this.updateItem({
      ...foundItem,
      elements: [...(foundItem.elements ?? []).filter(element => element.type !== 'propertystate')],
    });
  }
}

export interface AreaModel extends RestTemplateAreaModel {
  areaSurvey: AreaSurveyModel;
}
export interface AreaSurveyModel {
  title: string;
  pages: AreaPageModel[];
  logoPosition?: string;
  surveyId?: string;
}

export interface AreaPageModel {
  name: string;
  title: string;
  elements: AreaPageElementModel[];
}

export interface RestTemplateEditorRatingGroup {
  name: string;
  itemsJson: string;
  id: number;
}

export interface TemplateEditorRatingGroupItem {
  item: {
    backgroundColor: string;
    emoji: string;
    id: string;
    onSelect?: ('photoRequired' | 'commentRequired')[];
    text: string;
    isDefault?: boolean;
  };
}

export interface TemplateEditorRatingGroup {
  name: string;
  items: TemplateEditorRatingGroupItem[];
  id: number;
}

export interface TemplateEditorEmojiContent {
  id: 0;
  emojiCode: string;
  title: string;
  keywords: string[];
  assetRelativeUrl: string;
}

export interface AreaPageElementModel {
  type: 'file' | 'comment' | 'boolean' | 'propertystate';
  title: string;
  name: string;
  guid?: string;
  [key: string]: unknown;
}

export interface AreaPageElementRatingGroupModel extends AreaPageElementModel {
  type: 'propertystate';
  items: TemplateEditorRatingGroupItem[];
}

export function getInitialAreaData(title: string): AreaSurveyModel {
  return {
    title: title,
    logoPosition: 'right',
    pages: [
      {
        name: `page1`,
        title: 'Item 1',
        elements: [
          // default invisible items for mobile
          {
            type: 'file',
            name: 'Pictures:',
            title: 'Pictures:',
            description: 'Pictures:',
          },
          {
            type: 'comment',
            name: 'Enter a note:',
            title: 'Enter a note:',
          },
        ],
      },
    ],
  };
}
